import { useState } from 'react';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase-config';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { X, Loader2, User, CheckCircle2, RefreshCw } from 'lucide-react';
import { generateNickname, getNicknameInitials } from '../utils/nicknameGenerator';

interface ProfileModalProps {
  brandColor: string;
  onClose: () => void;
}

export default function ProfileModal({ brandColor, onClose }: ProfileModalProps) {
  const { user, userProfile, refreshUserProfile } = useAuth();
  const [nickname, setNickname] = useState(userProfile?.nickname || '');
  const [emailPreferences, setEmailPreferences] = useState({
    statusUpdates: userProfile?.emailPreferences?.statusUpdates ?? true,
    newComments: userProfile?.emailPreferences?.newComments ?? true,
    weeklyDigest: userProfile?.emailPreferences?.weeklyDigest ?? true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !userProfile) {
      setError('You must be logged in to update your profile');
      return;
    }

    const trimmedNickname = nickname.trim();
    if (!trimmedNickname) {
      setError('Nickname cannot be empty');
      return;
    }

    if (trimmedNickname.length < 2) {
      setError('Nickname must be at least 2 characters');
      return;
    }

    if (trimmedNickname.length > 50) {
      setError('Nickname must be less than 50 characters');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        nickname: trimmedNickname,
        emailPreferences: emailPreferences,
        updatedAt: Timestamp.now(),
      });

      await refreshUserProfile();

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
      }, 2000);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGenerateNew = () => {
    setNickname(generateNickname());
    setError(null);
  };

  if (!user || !userProfile) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center text-white text-lg font-bold"
              style={{ backgroundColor: brandColor }}
            >
              {getNicknameInitials(userProfile.nickname)}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Your Profile</h2>
              <p className="text-sm text-muted-foreground">{user.email}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Success Message */}
          {success && (
            <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
              <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-medium">Profile updated successfully!</span>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Nickname Field */}
          <div className="space-y-2">
            <Label htmlFor="nickname">
              Display Name <span className="text-destructive">*</span>
            </Label>
            <div className="flex gap-2">
              <Input
                id="nickname"
                value={nickname}
                onChange={(e) => {
                  setNickname(e.target.value);
                  setError(null);
                }}
                placeholder="Enter your display name"
                className={error ? 'border-destructive' : ''}
                disabled={isSubmitting}
                maxLength={50}
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleGenerateNew}
                disabled={isSubmitting}
                title="Generate new random nickname"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              This is how you'll appear to others when commenting and submitting requests
            </p>
          </div>

          {/* Preview */}
          <div className="bg-muted rounded-lg p-4">
            <p className="text-xs text-muted-foreground mb-2">Preview:</p>
            <div className="flex items-center gap-2">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
                style={{ backgroundColor: brandColor }}
              >
                {getNicknameInitials(nickname || 'Anonymous')}
              </div>
              <p className="text-sm font-medium text-foreground">
                {nickname || 'Anonymous'}
              </p>
            </div>
          </div>

          {/* Info */}
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
            <div className="flex gap-2">
              <User className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-600 dark:text-blue-400">
                <p className="font-medium mb-1">Privacy Note</p>
                <p className="text-xs opacity-90">
                  Your display name is public. Your email address is never shown to other users.
                </p>
              </div>
            </div>
          </div>

          {/* Email Preferences */}
          <div className="space-y-4 border-t border-border pt-6">
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-2">Email Notifications</h3>
              <p className="text-xs text-muted-foreground mb-4">
                Choose which email notifications you'd like to receive
              </p>
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={emailPreferences.statusUpdates}
                onChange={(e) =>
                  setEmailPreferences({ ...emailPreferences, statusUpdates: e.target.checked })
                }
                className="mt-1"
                style={{ accentColor: brandColor }}
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-foreground">Status Updates</div>
                <div className="text-xs text-muted-foreground">
                  Get notified when requests you created or voted on change status
                </div>
              </div>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={emailPreferences.newComments}
                onChange={(e) =>
                  setEmailPreferences({ ...emailPreferences, newComments: e.target.checked })
                }
                className="mt-1"
                style={{ accentColor: brandColor }}
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-foreground">New Comments</div>
                <div className="text-xs text-muted-foreground">
                  Get notified when someone comments on your requests or requests you've voted on
                </div>
              </div>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={emailPreferences.weeklyDigest}
                onChange={(e) =>
                  setEmailPreferences({ ...emailPreferences, weeklyDigest: e.target.checked })
                }
                className="mt-1"
                style={{ accentColor: brandColor }}
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-foreground">Weekly Digest</div>
                <div className="text-xs text-muted-foreground">
                  Receive a weekly summary of popular requests and updates
                </div>
              </div>
            </label>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !nickname.trim() || nickname === userProfile.nickname}
              style={{ backgroundColor: brandColor }}
              className="text-white"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
